import { PageConfig, PageKey } from "@apis:ui_branding/ui_branding";
import { Box, TextField } from "@mui/material";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IconTypeToggle } from "./IconTypeToggle";
import { PageConfigCard } from "./PageConfigCard";
import { PageTabs } from "./PageTabs";
import { PresetIconGrid } from "./PresetIconGrid";
import { SectionWrapper } from "./SectionWrapper";
import { FieldLabel } from "./shared-styles";
import { SvgUploadZone } from "./SvgUploadZone";
import { getPageLabel } from "./utils";

interface IconsSectionProps {
  activePage: PageKey;
  onPageChange: (page: PageKey) => void;
  iconType: "preset" | "custom";
  onIconTypeChange: (type: "preset" | "custom") => void;
  pages: Record<PageKey, PageConfig>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
}

export const IconsSection = ({
  activePage,
  onPageChange,
  iconType,
  onIconTypeChange,
  pages,
  control,
  setValue,
}: IconsSectionProps) => {
  const { t } = useTranslation();
  return (
    <SectionWrapper
      instruction={t("preferences.uiBranding.instructions.icons")}
    >
      <PageTabs activePage={activePage} onPageChange={onPageChange} />
      <PageConfigCard
        title={t("preferences.uiBranding.pageIconTitle", {
          page: getPageLabel(activePage, t),
        })}
      >
        <IconTypeToggle value={iconType} onChange={onIconTypeChange} />

        {iconType === "preset" ? (
          <Box>
            <FieldLabel sx={{ mb: 1 }}>
              {t("preferences.uiBranding.selectIconFor", {
                page: getPageLabel(activePage, t),
              })}
            </FieldLabel>
            <PresetIconGrid
              page={activePage}
              selectedKey={pages[activePage]?.iconKey}
              onSelect={(key) =>
                setValue(`pages.${activePage}.iconKey`, key, {
                  shouldDirty: true,
                })
              }
            />
          </Box>
        ) : (
          <Box>
            <FieldLabel sx={{ mb: 1 }}>
              {t("preferences.uiBranding.uploadCustomSvg")}
            </FieldLabel>
            <SvgUploadZone
              value={pages[activePage]?.customIconSvg}
              onChange={(base64) =>
                setValue(`pages.${activePage}.customIconSvg`, base64, {
                  shouldDirty: true,
                })
              }
              onRemove={() =>
                setValue(`pages.${activePage}.customIconSvg`, null, {
                  shouldDirty: true,
                })
              }
            />
          </Box>
        )}

        <Box sx={{ mt: 1.5 }}>
          <FieldLabel>{t("preferences.uiBranding.tooltipText")}</FieldLabel>
          <Controller
            key={`tooltip-${activePage}`}
            name={`pages.${activePage}.tooltip`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value || null)}
                fullWidth
                size="small"
                placeholder={t(
                  `preferences.uiBranding.pageDefaults.${activePage}.tooltip`,
                )}
                sx={{
                  "& .MuiOutlinedInput-root": { bgcolor: "white" },
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                    px: 1.25,
                    fontSize: "0.8rem",
                  },
                }}
              />
            )}
          />
        </Box>
      </PageConfigCard>
    </SectionWrapper>
  );
};
